import React, { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useLang } from "@/i18n";
import api from "@/lib/api";
import { ThemeToggle, LangToggle } from "@/components/Toggles";
import { PLANS } from "@/lib/constants";
import { Button } from "@/components/ui/button";
import {
  Droplets, Users, ListOrdered, Monitor, Printer, QrCode, ShoppingBag,
  Check, ArrowRight, Scale, Boxes, Star,
} from "lucide-react";

function TrackLookup() {
  const { t } = useLang();
  const [open, setOpen] = useState(false);
  const [code, setCode] = useState("");
  const go = () => {
    const c = code.trim().toUpperCase();
    if (c) window.open(`/track/${c}`, "_blank");
    setOpen(false); setCode("");
  };
  return (
    <div className="relative">
      <button data-testid="track-lookup-btn" onClick={() => setOpen((o) => !o)} title={t("track_order")}
        className="flex h-9 items-center gap-1.5 rounded-full border border-olive-border bg-olive-light px-3 text-sm font-medium text-olive transition-colors hover:bg-olive-light/70">
        <QrCode className="h-4 w-4" /> <span className="hidden sm:inline">{t("track_order")}</span>
      </button>
      {open && (
        <>
          <div className="fixed inset-0 z-40" onClick={() => setOpen(false)} />
          <div data-testid="track-panel" className="absolute right-0 z-50 mt-2 w-72 rounded-xl border border-border bg-card p-3 shadow-xl animate-fade-in">
            <p className="mb-1 flex items-center gap-2 text-sm font-semibold"><QrCode className="h-4 w-4 text-olive" /> {t("track_order")}</p>
            <p className="mb-2 text-xs text-muted-foreground">{t("track_hint")}</p>
            <div className="flex items-center gap-2">
              <input data-testid="track-code-input" autoFocus value={code}
                onChange={(e) => setCode(e.target.value)}
                onKeyDown={(e) => { if (e.key === "Enter") go(); }}
                placeholder={t("track_placeholder")}
                className="h-9 flex-1 rounded-lg border border-border bg-background px-3 text-sm uppercase outline-none focus:ring-2 focus:ring-primary/40" />
              <button data-testid="track-go-btn" onClick={go}
                className="h-9 rounded-lg bg-primary px-3 text-sm font-semibold text-primary-foreground hover:bg-olive-hover">{t("track_go")}</button>
            </div>
          </div>
        </>
      )}
    </div>
  );
}

const FEATURES = [
  { icon: Users, t: "f_clients_t", d: "f_clients_d" },
  { icon: ListOrdered, t: "f_queue_t", d: "f_queue_d" },
  { icon: Monitor, t: "f_tv_t", d: "f_tv_d" },
  { icon: Printer, t: "f_print_t", d: "f_print_d" },
  { icon: QrCode, t: "f_qr_t", d: "f_qr_d" },
  { icon: ShoppingBag, t: "f_store_t", d: "f_store_d" },
];

const MODULE_LABELS = {
  live_queue: "mod_live_queue", thermal_printing: "mod_thermal_printing",
  analytics: "mod_analytics", store: "mod_store",
};

export default function Landing() {
  const { t } = useLang();
  const navigate = useNavigate();
  const [prices, setPrices] = useState({});
  useEffect(() => {
    api.get("/plans").then((r) => {
      const m = {};
      r.data.forEach((p) => { m[p.plan_id] = p.price; });
      setPrices(m);
    }).catch(() => {});
  }, []);
  const HERO_IMG = "https://images.pexels.com/photos/31667163/pexels-photo-31667163.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940";

  return (
    <div className="min-h-screen bg-background grain-bg">
      {/* Nav */}
      <header className="sticky top-0 z-40 border-b border-border/60 bg-background/80 backdrop-blur-md">
        <div className="mx-auto flex h-16 max-w-7xl items-center justify-between px-4 sm:px-6 lg:px-8">
          <div className="flex items-center gap-2.5">
            <div className="flex h-9 w-9 items-center justify-center rounded-lg bg-primary text-primary-foreground">
              <Droplets className="h-5 w-5" />
            </div>
            <span className="font-heading text-xl font-bold tracking-tight">e-olive Albania</span>
          </div>
          <div className="flex items-center gap-2 sm:gap-3">
            <TrackLookup />
            <LangToggle />
            <ThemeToggle />
            <Button data-testid="nav-login-btn" variant="ghost" onClick={() => navigate("/login")} className="hidden sm:inline-flex">
              {t("login")}
            </Button>
            <Button data-testid="nav-signup-btn" onClick={() => navigate("/login?mode=signup")} className="rounded-full bg-primary hover:bg-olive-hover">
              {t("hero_cta")}
            </Button>
          </div>
        </div>
      </header>

      {/* Hero */}
      <section className="hero-radial">
        <div className="mx-auto grid max-w-7xl items-center gap-10 px-4 py-16 sm:px-6 lg:grid-cols-2 lg:gap-12 lg:px-8 lg:py-24">
          <div className="animate-fade-up">
            <span className="inline-flex items-center gap-2 rounded-full border border-olive-border bg-olive-light px-3 py-1 text-xs font-semibold uppercase tracking-wider text-olive" data-testid="hero-badge">
              <Droplets className="h-3.5 w-3.5" /> {t("hero_badge")}
            </span>
            <h1 className="mt-5 font-heading text-4xl font-extrabold tracking-tight sm:text-5xl lg:text-6xl">
              {t("hero_title")}
            </h1>
            <p className="mt-5 max-w-xl text-base leading-relaxed text-muted-foreground sm:text-lg">
              {t("hero_sub")}
            </p>
            <div className="mt-8 flex flex-wrap items-center gap-3">
              <Button data-testid="hero-cta-btn" size="lg" onClick={() => navigate("/login?mode=signup")} className="rounded-full bg-primary px-6 hover:bg-olive-hover">
                {t("hero_cta")} <ArrowRight className="ml-1 h-4 w-4" />
              </Button>
              <Button data-testid="hero-demo-btn" size="lg" variant="outline" onClick={() => navigate("/live-queue/devpos-mill")} className="rounded-full border-olive-border px-6">
                <Monitor className="mr-1.5 h-4 w-4" /> {t("hero_secondary")}
              </Button>
            </div>
            <div className="mt-8 flex flex-wrap gap-6 text-sm text-muted-foreground">
              <span className="flex items-center gap-2"><Scale className="h-4 w-4 text-amber" /> Crate weighing</span>
              <span className="flex items-center gap-2"><QrCode className="h-4 w-4 text-amber" /> QR tracking</span>
              <span className="flex items-center gap-2"><Boxes className="h-4 w-4 text-amber" /> Live queue</span>
            </div>
          </div>
          <div className="relative animate-fade-in">
            <div className="overflow-hidden rounded-2xl border border-olive-border shadow-2xl">
              <img src={HERO_IMG} alt="Olive harvest" className="h-[420px] w-full object-cover" />
            </div>
            <div className="absolute -bottom-5 -left-5 hidden rounded-xl border border-border bg-card p-4 shadow-xl sm:block">
              <div className="flex items-center gap-3">
                <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-stage-bottling_ready/15">
                  <Check className="h-5 w-5" style={{ color: "#10B981" }} />
                </div>
                <div>
                  <p className="font-mono text-xs uppercase tracking-wider text-muted-foreground">Batch OL4F2A</p>
                  <p className="text-sm font-semibold">Ready for bottling · 128 kg</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Features */}
      <section className="mx-auto max-w-7xl px-4 py-16 sm:px-6 lg:px-8 lg:py-24">
        <div className="max-w-2xl">
          <h2 className="font-heading text-3xl font-bold tracking-tight sm:text-4xl">{t("features_title")}</h2>
          <p className="mt-3 text-muted-foreground">{t("features_sub")}</p>
        </div>
        <div className="mt-10 grid gap-5 sm:grid-cols-2 lg:grid-cols-3">
          {FEATURES.map((f, i) => (
            <div
              key={f.t}
              data-testid={`feature-card-${i}`}
              className="group rounded-xl border border-border bg-card p-6 transition-all hover:-translate-y-1 hover:border-olive-border hover:shadow-lg"
            >
              <div className="flex h-11 w-11 items-center justify-center rounded-lg bg-olive-light text-olive transition-colors group-hover:bg-primary group-hover:text-primary-foreground">
                <f.icon className="h-5 w-5" />
              </div>
              <h3 className="mt-4 font-heading text-lg font-semibold">{t(f.t)}</h3>
              <p className="mt-2 text-sm leading-relaxed text-muted-foreground">{t(f.d)}</p>
            </div>
          ))}
        </div>
      </section>

      {/* Pricing */}
      <section className="border-y border-border bg-secondary/40">
        <div className="mx-auto max-w-7xl px-4 py-16 sm:px-6 lg:px-8 lg:py-24">
          <div className="mx-auto max-w-2xl text-center">
            <h2 className="font-heading text-3xl font-bold tracking-tight sm:text-4xl">{t("pricing_title")}</h2>
            <p className="mt-3 text-muted-foreground">{t("pricing_sub")}</p>
          </div>
          <div className="mt-12 grid gap-6 lg:grid-cols-3">
            {PLANS.map((plan) => (
              <div
                key={plan.id}
                data-testid={`plan-card-${plan.id}`}
                className={`relative rounded-2xl border bg-card p-8 transition-all ${
                  plan.highlight ? "border-primary shadow-xl lg:-translate-y-3" : "border-border"
                }`}
              >
                {plan.highlight && (
                  <span className="absolute -top-3 left-1/2 -translate-x-1/2 rounded-full bg-primary px-3 py-1 text-xs font-semibold text-primary-foreground">
                    <Star className="mr-1 inline h-3 w-3" /> {t("most_popular")}
                  </span>
                )}
                <h3 className="font-heading text-xl font-bold">{t(`plan_${plan.id}`)}</h3>
                <p className="mt-1 text-sm text-muted-foreground">{t(`plan_${plan.id}_d`)}</p>
                <div className="mt-5 flex items-end gap-1">
                  <span className="font-heading text-4xl font-extrabold">{prices[plan.id] != null ? `€${prices[plan.id]}` : plan.price}</span>
                  <span className="mb-1 text-sm text-muted-foreground">{t("per_month")}</span>
                </div>
                <ul className="mt-6 space-y-3">
                  {plan.modules.map((m) => (
                    <li key={m} className="flex items-center gap-2.5 text-sm">
                      <Check className="h-4 w-4 shrink-0 text-primary" /> {t(MODULE_LABELS[m])}
                    </li>
                  ))}
                </ul>
                <Button
                  data-testid={`plan-cta-${plan.id}`}
                  onClick={() => navigate("/login?mode=signup")}
                  className={`mt-8 w-full rounded-full ${plan.highlight ? "bg-primary hover:bg-olive-hover" : "bg-secondary text-foreground hover:bg-secondary/70"}`}
                >
                  {t("get_started")}
                </Button>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="mx-auto max-w-7xl px-4 py-10 sm:px-6 lg:px-8">
        <div className="flex flex-col items-center justify-between gap-4 border-t border-border pt-8 sm:flex-row">
          <div className="flex items-center gap-2.5">
            <div className="flex h-8 w-8 items-center justify-center rounded-lg bg-primary text-primary-foreground">
              <Droplets className="h-4 w-4" />
            </div>
            <span className="font-heading font-bold">e-olive Albania</span>
          </div>
          <p className="text-sm text-muted-foreground">{t("footer_note")} © 2026</p>
        </div>
      </footer>
    </div>
  );
}
